import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

// Components
import { ToastProvider } from './components/Toast';
import NotificationBell from './features/notifications/NotificationBell';
import NotificationDrawer from './features/notifications/NotificationDrawer';
import OfflineBanner from './features/offline/OfflineBanner';
import BookShell from './animations/BookShell';
import { PrivateRoute, GroupRoute, RoleRoute } from './components/PrivateRoute';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OnboardingPage from './pages/OnboardingPage';
import LeaderPanel from './pages/LeaderPanel';
import Profile from './pages/Profile';
import JoinPage from './pages/JoinPage';
import StudyPage from './pages/StudyPage';
import LeaderboardPage from './pages/LeaderboardPage';
import ChatPage from './pages/ChatPage';
import AdminPage from './pages/AdminPage';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const { token, role, fetchUser, isLoading } = useAuthStore();

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#090e1a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ToastProvider>
        <ScrollToTop />
        <OfflineBanner />
        
        {/* Global UI for authenticated users */}
        {token && role !== 'admin' && (
          <div className="fixed top-4 right-4 z-50 flex items-center gap-4">
            <NotificationBell />
          </div>
        )}
        <NotificationDrawer />

        <div className="min-h-screen text-white bg-[#090e1a] relative overflow-hidden app-container">
          {/* Subtle noise grain overlay */}
          <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")' }} />
          
          {/* Global Background Glow Orbs for premium glassmorphism refraction */}
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden select-none">
            {/* Top-left slow pulsing Indigo Orb */}
            <div 
              className="absolute w-[550px] h-[550px] rounded-full bg-indigo-500/10 blur-[130px] bg-glow-orb" 
              style={{
                top: '-150px',
                left: '-100px',
                animationDuration: '24s',
              }}
            />
            {/* Bottom-right slow pulsing Purple Orb */}
            <div 
              className="absolute w-[650px] h-[650px] rounded-full bg-purple-500/10 blur-[150px] bg-glow-orb" 
              style={{
                bottom: '-200px',
                right: '-100px',
                animationDuration: '32s',
                animationDelay: '-5s',
              }}
            />
            {/* Mid-right Amber Orb for study-themed warm highlights */}
            <div 
              className="absolute w-[400px] h-[400px] rounded-full bg-amber-500/5 blur-[110px] bg-glow-orb" 
              style={{
                top: '25%',
                right: '5%',
                animationDuration: '28s',
                animationDelay: '-10s',
              }}
            />
            {/* Left-center Emerald Orb for streak energy reflection */}
            <div 
              className="absolute w-[350px] h-[350px] rounded-full bg-emerald-500/5 blur-[100px] bg-glow-orb" 
              style={{
                bottom: '30%',
                left: '10%',
                animationDuration: '20s',
                animationDelay: '-3s',
              }}
            />
          </div>

          <div className="relative z-10 w-full h-full">
            <BookShell>
              <Routes>
                <Route path="/" element={<LoginPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/onboarding" element={
                  <PrivateRoute>
                    <OnboardingPage />
                  </PrivateRoute>
                } />
                <Route path="/dashboard" element={
                  <GroupRoute>
                    <DashboardPage />
                  </GroupRoute>
                } />
                <Route path="/study" element={
                  <GroupRoute>
                    <StudyPage />
                  </GroupRoute>
                } />
                <Route path="/leaderboard" element={
                  <GroupRoute>
                    <LeaderboardPage />
                  </GroupRoute>
                } />
                <Route path="/chat" element={
                  <GroupRoute>
                    <ChatPage />
                  </GroupRoute>
                } />
                <Route path="/leader" element={
                  <RoleRoute requiredRole="leader">
                    <LeaderPanel />
                  </RoleRoute>
                } />
                <Route path="/profile" element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                } />
                <Route path="/admin" element={
                  <RoleRoute requiredRole="admin">
                    <AdminPage />
                  </RoleRoute>
                } />
                <Route path="/join/:code" element={<JoinPage />} />
                <Route path="*" element={<LoginPage />} />
              </Routes>
            </BookShell>
          </div>
        </div>
      </ToastProvider>
    </BrowserRouter>
  );
}
